/**
 * MyFinance.jsx — Çalışan'ın kendi finansal verilerini görüntülemesi.
 *
 * Backend `/employee-profile/finance/{user_id}` zaten `is_own` durumunu kabul
 * ediyor. Firma `employee_finance_visible` ayarı kapalıysa 403 döner ve
 * burada açıklayıcı bir UI gösterilir.
 *
 * Şub 09, 2026 — admin profil finans tabının read-only kopyası; ekleme/silme
 * butonları yok, filtre + özet kartlar + işlem listesi var.
 */
import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, Euro, Wallet, Zap, Calendar, Info, ShieldOff, Printer } from 'lucide-react';
import api from '../lib/api';
import { useAuthStore } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { Money } from '../components/Money';

const FINANCE_TYPE_KEYS = {
  avans: { key: 'advance', color: 'bg-yellow-500' },
  pot_plus: { key: 'potPlus', color: 'bg-emerald-500' },
  pot_minus: { key: 'potMinus', color: 'bg-rose-500' },
  laadvergoeding: { key: 'laadvergoeding', color: 'bg-cyan-500' },
  leasing: { key: 'leasing', color: 'bg-purple-500' },
  diger: { key: 'other', color: 'bg-slate-400' },
};

const formatLocalDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default function MyFinance() {
  const { user } = useAuthStore();
  const { t } = useTranslation();
  const [finance, setFinance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hiddenByFirm, setHiddenByFirm] = useState(false);

  const [filter, setFilter] = useState('all');
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setHiddenByFirm(false);
      try {
        const r = await api.get(`/employee-profile/finance/${user.id}`);
        if (!cancelled) setFinance(r.data?.data || null);
      } catch (e) {
        if (!cancelled && e?.response?.status === 403) setHiddenByFirm(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  const formatCurrency = (n) =>
    new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'EUR' }).format(Number(n) || 0);
  const formatDate = (s) => {
    if (!s) return '-';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleDateString('tr-TR');
  };

  // Apply filter
  const filtered = useMemo(() => {
    if (!finance) return null;
    let entries = [...(finance.entries || [])];
    if (filter !== 'all') {
      entries = entries.filter((entry) => {
        if (!entry.date) return false;
        const ed = new Date(entry.date);
        if (Number.isNaN(ed.getTime())) return false;
        const today = new Date();
        if (filter === 'lastMonth') {
          const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          return ed.getFullYear() === lm.getFullYear() && ed.getMonth() === lm.getMonth();
        }
        if (filter === 'month') {
          return ed.getMonth() + 1 === month && ed.getFullYear() === year;
        }
        if (filter === 'year') {
          return ed.getFullYear() === year;
        }
        if (filter === 'range') {
          if (dateFrom && formatLocalDate(ed) < dateFrom) return false;
          if (dateTo && formatLocalDate(ed) > dateTo) return false;
          return true;
        }
        return true;
      });
    }
    // Recompute summary client-side from filtered entries
    const sum = { total_avans: 0, pot_plus: 0, pot_minus: 0, laadvergoeding: 0 };
    for (const e of entries) {
      const amt = Number(e.amount) || 0;
      if (e.entry_type === 'avans') sum.total_avans += amt;
      else if (e.entry_type === 'pot_plus') sum.pot_plus += amt;
      else if (e.entry_type === 'pot_minus') sum.pot_minus += amt;
      else if (e.entry_type === 'laadvergoeding') sum.laadvergoeding += amt;
    }
    sum.pot_balance = sum.pot_plus - sum.pot_minus;
    return { entries, summary: sum };
  }, [finance, filter, month, year, dateFrom, dateTo]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (hiddenByFirm) {
    return (
      <Card className="max-w-xl mx-auto mt-12">
        <CardContent className="py-10 text-center space-y-3">
          <ShieldOff className="h-10 w-10 mx-auto text-slate-400" />
          <h2 className="text-lg font-semibold text-slate-700 dark:text-slate-200">
            {t('myFinance.hiddenTitle') || 'Finansal veriler şu an gizli'}
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {t('myFinance.hiddenDesc') ||
              'Firma yöneticiniz çalışan finansal verilerinin görüntülenmesini kapatmış. Sorularınız için yöneticinizle iletişime geçin.'}
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="my-finance-page">
      {/* Print-specific styles: yazdırırken sadece print-area görünür, sidebar/header gizli */}
      <style>{`
        @media print {
          body * { visibility: hidden !important; }
          #my-finance-print-area, #my-finance-print-area * { visibility: visible !important; }
          #my-finance-print-area { position: absolute; left: 0; top: 0; width: 100%; padding: 1rem; }
          .no-print { display: none !important; }
          @page { margin: 12mm; }
        }
      `}</style>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('myFinance.title') || 'Finansal Verilerim'}
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            {t('myFinance.subtitle') || 'Avans, POT, laadvergoeding ve diğer kayıtlarınız'}
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => window.print()}
          className="no-print gap-1.5"
          data-testid="my-finance-print-btn"
          title="PDF olarak kaydet (yazdırma diyaloğunda 'PDF olarak Kaydet'i seçin)"
        >
          <Printer className="h-4 w-4" />
          {t('myFinance.savePdf') || 'PDF Olarak İndir'}
        </Button>
      </div>

      <div id="my-finance-print-area" className="space-y-6">

      {/* Filter */}
      <Card className="no-print">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-slate-500 dark:text-slate-400" />
              <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                {t('common.filter') || 'Filtre'}:
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { k: 'all', label: t('common.all') || 'Tümü' },
                { k: 'lastMonth', label: t('common.lastMonth') || 'Geçen Ay' },
                { k: 'month', label: t('common.month') || 'Ay' },
                { k: 'year', label: t('common.year') || 'Yıl' },
                { k: 'range', label: t('common.dateRange') || 'Tarih Aralığı' },
              ].map((b) => (
                <Button
                  key={b.k}
                  size="sm"
                  variant={filter === b.k ? 'default' : 'outline'}
                  onClick={() => setFilter(b.k)}
                  data-testid={`my-finance-filter-${b.k}`}
                >
                  {b.label}
                </Button>
              ))}
            </div>
            {(filter === 'month' || filter === 'year') && (
              <div className="flex items-center gap-2 ml-auto">
                {filter === 'month' && (
                  <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((m) => (
                        <SelectItem key={m} value={String(m)}>
                          {new Date(2024, m - 1).toLocaleString('tr', { month: 'long' })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
                <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
                  <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {[2024, 2025, 2026, 2027].map((y) => (
                      <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            {filter === 'range' && (
              <div className="flex items-center gap-2 ml-auto">
                <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-36" />
                <span className="text-slate-400">-</span>
                <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="w-36" />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Summary */}
      {filtered && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="border-l-4 border-l-yellow-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('employeeProfile.advance') || 'Avans'}
                  </p>
                  <p className="text-2xl font-bold text-yellow-600">
                    <Money amount={filtered.summary.total_avans || 0} />
                  </p>
                </div>
                <Euro className="h-5 w-5 text-yellow-500" />
              </CardContent>
            </Card>

            <Card
              className={`border-l-4 ${
                (filtered.summary.pot_balance || 0) >= 0 ? 'border-l-emerald-500' : 'border-l-rose-500'
              }`}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <div className="flex items-center gap-1">
                    <p className="text-sm text-slate-500 dark:text-slate-400">POT</p>
                    <Info className="h-3 w-3 text-slate-400" />
                  </div>
                  <p
                    className={`text-2xl font-bold ${
                      (filtered.summary.pot_balance || 0) >= 0 ? 'text-emerald-600' : 'text-rose-600'
                    }`}
                  >
                    <Money amount={filtered.summary.pot_balance || 0} prefix={(filtered.summary.pot_balance || 0) >= 0 ? '+' : ''} />
                  </p>
                  <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5">
                    <Money amount={filtered.summary.pot_plus || 0} prefix="+" /> / <Money amount={filtered.summary.pot_minus || 0} prefix="−" />
                  </p>
                </div>
                <Wallet
                  className={`h-5 w-5 ${
                    (filtered.summary.pot_balance || 0) >= 0 ? 'text-emerald-500' : 'text-rose-500'
                  }`}
                />
              </CardContent>
            </Card>

            <Card className="border-l-4 border-l-cyan-500">
              <CardContent className="p-4 flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('employeeProfile.laadvergoeding') || 'Laadvergoeding'}
                  </p>
                  <p className="text-2xl font-bold text-cyan-600">
                    <Money amount={filtered.summary.laadvergoeding || 0} />
                  </p>
                </div>
                <Zap className="h-5 w-5 text-cyan-500" />
              </CardContent>
            </Card>
          </div>

          {/* Entries */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {t('employeeProfile.transactionHistory') || 'İşlem Geçmişi'}
                <Badge variant="secondary">
                  {filtered.entries.length} {t('common.records') || 'kayıt'}
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {filtered.entries.length === 0 ? (
                <p className="text-slate-400 text-center py-8">
                  {t('employeeProfile.noTransactions') || 'Kayıt bulunmuyor'}
                </p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('common.type') || 'Tür'}</TableHead>
                      <TableHead>{t('common.amount') || 'Tutar'}</TableHead>
                      <TableHead>{t('common.date') || 'Tarih'}</TableHead>
                      <TableHead>{t('common.description') || 'Açıklama'}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.entries.map((entry, idx) => {
                      const typeInfo = FINANCE_TYPE_KEYS[entry.entry_type] || FINANCE_TYPE_KEYS.diger;
                      const isPositive = ['odeme', 'prim', 'pot_plus', 'laadvergoeding'].includes(entry.entry_type);
                      return (
                        <TableRow key={idx} data-testid={`my-finance-row-${idx}`}>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className={`h-3 w-3 rounded-full ${typeInfo.color}`} />
                              {t(`employeeProfile.${typeInfo.key}`) || entry.entry_type}
                            </div>
                          </TableCell>
                          <TableCell className={isPositive ? 'text-green-600 font-medium' : 'text-red-600 font-medium'}>
                            <Money amount={entry.amount} prefix={isPositive ? '+' : '-'} revealKey={`my-finance-row-${idx}`} />
                          </TableCell>
                          <TableCell>{formatDate(entry.date)}</TableCell>
                          <TableCell>{entry.description || '-'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </>
      )}
      </div>
    </div>
  );
}
